import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import ScatterPlot from './ScatterPlot';
import PieChart from './PieChart';
import Histogram from './Histogram';
import DotPlot from './DotPlot';
import BarChart from './BarChart';
import BoxPlot from './BoxPlot';
import ProbabilityPlot from './ProbabilityPlot';

import AlertPanel from './AlertPanel';

import { graphTypes } from './AlertPanel/Strings';

import { getActiveChartsAndGraphsTab } from 'store/scenes/graphicalStatistics/rightPane/selectors';
import {
  setActiveChartsAndGraphsTab
} from 'store/scenes/graphicalStatistics/rightPane/actions';

import {
  getTypeOfFrequencyBarChart,
  getTypeOfFrequencyPieChart,
  getTypeOfFrequencyHistogram
} from 'store/scenes/graphicalStatistics/settingsPane/selectors';
import { getDatasetType } from 'store/dataset';

import RightPaneComponent from './Component';
import { FREQUENCY } from 'scenes/GraphicalStatistics/SettingsForm/AdditionalFields/Component';

const RightPaneContainer = props => {
  const {
    showGraphs,
    activeChartsAndGraphsTab,
    isFrequencyTable,
    isTypeOfGraphsFrequencyBarChart,
    isTypeOfGraphsFrequencyPieChart,
    isTypeOfGraphsFrequencyHistogram,
    typeOfFrequencyDataSet,

    firstParameterName = null,
    secondParameterName = null,
    secondParameterType = null,
    isFirstParamNumerical,
    isFirstParamCategorical,
    isSecondParamNumerical,
    isSecondParamCategorical
  } = props;

  useEffect(() => {
    if (
      isFrequencyTable &&
      !(activeChartsAndGraphsTab === 5 || activeChartsAndGraphsTab === 4)
    ) {
      props.setActiveChartsAndGraphsTab(4);
    }
  }, []);


  const buildAlertPanel = graphType => (
    <AlertPanel
      graphType={graphType}
      isFirstParamNumerical={isFirstParamNumerical}
      isSecondParamNumerical={isSecondParamNumerical}
      isFirstParamCategorical={isFirstParamCategorical}
      isSecondParamCategorical={isSecondParamCategorical}
      isFrequencyTable={isFrequencyTable}
    />
  );

  const renderScatterPlot = () => {
    let componentToRender = null;

    // scatter plot is possible when both params are numerical
    if (isFirstParamNumerical && isSecondParamNumerical && showGraphs) {
      componentToRender = (
        <ScatterPlot
          firstParameter={firstParameterName}
          secondParameter={secondParameterName}
        />
      );
    } else {
      componentToRender = buildAlertPanel(graphTypes.scatterPlot);
    }

    return componentToRender;
  };

  const renderPieChart = () => {
    let componentToRender = null;

    // pie chart is possible only when one param is chosen and the param is categorical
    if (isFirstParamCategorical && showGraphs) {
      componentToRender = (
        <PieChart
          parameter={firstParameterName}
          isTypeOfGraphsFrequency={isTypeOfGraphsFrequencyPieChart}
          isFrequencyTable={isFrequencyTable}
        />
      );
    } else {
      componentToRender = buildAlertPanel(graphTypes.pieChart);
    }

    return componentToRender;
  };

  const renderHistogram = () => {
    let componentToRender = null;

    // histogram is possible when either one numerical parameter is chosen, or with the mix of num and categorical params
    if (
      isFirstParamNumerical &&
      (isSecondParamCategorical || !secondParameterType) &&
      showGraphs
    ) {
      componentToRender = (
        <Histogram
          numericalParameter={firstParameterName}
          categoricalParameter={secondParameterName}
          isTypeOfGraphsFrequency={isTypeOfGraphsFrequencyHistogram}
        />
      );
      // account for field interchangeability (num+cat === cat+num)
    } else if (
      isFirstParamCategorical &&
      isSecondParamNumerical &&
      showGraphs
    ) {
      componentToRender = (
        <Histogram
          numericalParameter={secondParameterName}
          categoricalParameter={firstParameterName}
          isTypeOfGraphsFrequency={isTypeOfGraphsFrequencyHistogram}
        />
      );
    } else {
      componentToRender = buildAlertPanel(graphTypes.histogram);
    }

    return componentToRender;
  };

  const renderDotPlot = () => {
    let componentToRender = null;

    // dot plot is possible only when one param is chosen and the param is numerical
    if (isFirstParamNumerical && showGraphs) {
      componentToRender = <DotPlot parameter={firstParameterName} />;
    } else {
      componentToRender = buildAlertPanel(graphTypes.dotPlot);
    }

    return componentToRender;
  };

  const renderBarChart = () => {
    let componentToRender = null;

    // bar chart is possible for either one categorical param or both categorical params
    if (
      isFirstParamCategorical &&
      (!secondParameterType || isSecondParamCategorical) &&
      showGraphs
    ) {
      componentToRender = (
        <BarChart
          firstParameterName={firstParameterName}
          secondParameterName={secondParameterName}
          isTypeOfGraphsFrequency={isTypeOfGraphsFrequencyBarChart}
          typeOfFrequencyDataSet={typeOfFrequencyDataSet}
          isFrequencyTable={isFrequencyTable}
        />
      );
    } else {
      componentToRender = buildAlertPanel(graphTypes.barChart);
    }

    return componentToRender;
  };

  const renderBoxPlot = () => {
    let componentToRender = null;

    // box plot is possible when either one num parameter is chosen, or with the mix of num and cat params
    if (
      isFirstParamNumerical &&
      (isSecondParamCategorical || !secondParameterType) &&
      showGraphs
    ) {
      componentToRender = (
        <BoxPlot
          numericalParameter={firstParameterName}
          categoricalParameter={secondParameterName}
        />
      );
      // account for field interchangeability (num+cat === cat+num)
    } else if (
      isFirstParamCategorical &&
      isSecondParamNumerical &&
      showGraphs
    ) {
      componentToRender = (
        <BoxPlot
          numericalParameter={secondParameterName}
          categoricalParameter={firstParameterName}
        />
      );
    } else {
      componentToRender = buildAlertPanel(graphTypes.boxPlot);
    }

    return componentToRender;
  };

  const renderProbabilityPlot = () => {
    let componentToRender = null;

    // dot plot is possible only when one param is chosen and the param is numerical
    if (isFirstParamNumerical && showGraphs) {
      componentToRender = <ProbabilityPlot parameter={firstParameterName} />;
    } else {
      componentToRender = buildAlertPanel(graphTypes.probabilityPlot);
    }

    return componentToRender;
  };

  const onTabChange = activeIndex => {
    return props.setActiveChartsAndGraphsTab(activeIndex);
  };

  return (
    <RightPaneComponent
      activeIndex={activeChartsAndGraphsTab}
      handleTabChange={onTabChange}
      renderBarChart={renderBarChart}
      renderDotPlot={renderDotPlot}
      renderBoxPlot={renderBoxPlot}
      renderHistogram={renderHistogram}
      renderPieChart={renderPieChart}
      renderScatterPlot={renderScatterPlot}
      renderProbabilityPlot={renderProbabilityPlot}
      iconsSize={20}
      isFrequencyTable={isFrequencyTable}
    />
  );
};

RightPaneContainer.propTypes = {
  firstParameterName: PropTypes.string,
  secondParameterType: PropTypes.string,
  secondParameterName: PropTypes.string,

  isFirstParamNumerical: PropTypes.bool.isRequired,
  isFirstParamCategorical: PropTypes.bool.isRequired,
  isSecondParamNumerical: PropTypes.bool.isRequired,
  isSecondParamCategorical: PropTypes.bool.isRequired,

  setActiveChartsAndGraphsTab: PropTypes.func.isRequired,
  activeChartsAndGraphsTab: PropTypes.number.isRequired,
  showGraphs: PropTypes.bool.isRequired,
  isFrequencyTable: PropTypes.bool.isRequired,
  isTypeOfGraphsFrequencyBarChart: PropTypes.bool.isRequired,
  isTypeOfGraphsFrequencyPieChart: PropTypes.bool.isRequired,
  isTypeOfGraphsFrequencyHistogram: PropTypes.bool.isRequired,
  typeOfFrequencyDataSet: PropTypes.string
};

const mapStateToProps = state => ({
  typeOfFrequencyDataSet: getDatasetType(state),
  activeChartsAndGraphsTab: getActiveChartsAndGraphsTab(state),
  isTypeOfGraphsFrequencyBarChart:
    getTypeOfFrequencyBarChart(state) === FREQUENCY,
  isTypeOfGraphsFrequencyPieChart:
    getTypeOfFrequencyPieChart(state) === FREQUENCY,
  isTypeOfGraphsFrequencyHistogram:
    getTypeOfFrequencyHistogram(state) === FREQUENCY
});

const mapDispatchToProps = {
  setActiveChartsAndGraphsTab
};

export default connect(mapStateToProps, mapDispatchToProps)(RightPaneContainer);
